import React, { useEffect, useMemo, useRef, useState } from "react";
import { assetUrl } from "@/lib/api";

/**
 * RewardWheel — premium SVG spin wheel.
 *
 * Public API (kept 100% compatible with the old WheelCanvas component
 * so the existing PlayPage / backend spin logic never has to change):
 *
 *   props.slices        Array<{ label, gift_name?, image_url?, color_hex?, is_better_luck? }>
 *   props.logoUrl       string | null   — drawn in the center hub
 *   props.primaryColor  string           — falls back to CityDooh gold
 *   props.spinningTo    number | null    — WINNING slice index chosen by
 *                                         the BACKEND. When this becomes a
 *                                         number the wheel animates to it.
 *                                         The frontend never picks a winner.
 *   props.onSpinEnd     () => void       — fires once at animation end.
 *   props.spinDuration  ms (default 5200)
 *   props.testId        string
 *
 * IMPORTANT: this component is intentionally READ-ONLY with respect to
 * game logic. It merely animates the geometry so the visual pointer aligns
 * with the pre-decided `spinningTo` slice. It does NOT compute rewards,
 * probabilities, or eligibility.
 */

const CITYDOOH_GOLD = "#F5A800";
const CITYDOOH_GOLD_DEEP = "#c98a00";
const CITYDOOH_INK = "#0A0A0A";

export const RewardWheel = ({
  slices = [],
  logoUrl = null,
  primaryColor = CITYDOOH_GOLD,
  secondaryColor = null,
  onSliceText = null,
  spinningTo = null,
  onSpinEnd,
  spinDuration = 5200,
  testId = "wheel-canvas",
}) => {
  const wheelRef = useRef(null);
  const angleRef = useRef(0);           // current rotation in radians
  const animatingRef = useRef(false);
  const [logoOk, setLogoOk] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Respect the OS-level "reduce motion" preference — spec requirement.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const n = Math.max(slices.length, 1);
  const arc = (Math.PI * 2) / n;

  // Alternating premium palette: driven by SafeColors-normalized theme
  // colors from PlayPage. When `secondaryColor` is provided, we use
  // primary/secondary alternation; otherwise fall back to
  // primary/CityDooh-gold. Better-luck slices always dim to neutral.
  const palette = useMemo(() => {
    const a = primaryColor || CITYDOOH_GOLD;
    const b = secondaryColor || CITYDOOH_GOLD;
    return slices.map((s, i) => {
      if (s.is_better_luck) return "#3a3a3a";
      if (s.color_hex) return s.color_hex;
      return i % 2 === 0 ? a : b;
    });
  }, [slices, primaryColor, secondaryColor]);

  /**
   * When `spinningTo` transitions from null → a number, the backend has
   * decided the reward. Compute the exact target angle so the CENTER of
   * that slice ends up under the fixed 12-o'clock pointer, then apply a
   * CSS transform rotation. We use easing on a CSS `transition` for
   * buttery-smooth motion without a per-frame RAF loop.
   *
   * Geometry:
   *   • Slice i center angle (from 0-rad = 3 o'clock) = i*arc + arc/2
   *   • Pointer sits at -π/2 (12 o'clock)
   *   • We rotate the wheel by θ so slice-center + θ ≡ -π/2 (mod 2π)
   *     ⇒ θ_target_mod = (-π/2 - sliceCenter) mod 2π
   *
   * We ALWAYS add whole extra turns so the motion always feels like a
   * spin, never a shortcut. Whole turns don't change the resting angle
   * modulo 2π so alignment stays exact.
   */
  useEffect(() => {
    if (spinningTo == null) return;
    if (animatingRef.current) return;
    animatingRef.current = true;

    const target = spinningTo * arc + arc / 2;
    const initial = angleRef.current;
    const initialMod = ((initial % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let targetMod = (-Math.PI / 2 - target) % (Math.PI * 2);
    if (targetMod < 0) targetMod += Math.PI * 2;
    let delta = targetMod - initialMod;
    if (delta <= 0) delta += Math.PI * 2;

    // 4–6 additional full turns — always integer so mod-2π stays clean.
    const extraTurns = reduceMotion ? 0 : 4 + Math.floor(Math.random() * 3);
    const finalAngle = initial + delta + Math.PI * 2 * extraTurns;
    const duration = reduceMotion ? 500 : spinDuration;

    const el = wheelRef.current;
    if (!el) {
      animatingRef.current = false;
      return;
    }

    // Kick off the transition on the next paint so the browser registers
    // the from/to values (avoids the transition being skipped when React
    // batches multiple state updates in the same tick).
    requestAnimationFrame(() => {
      el.style.transition = `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      el.style.transform = `rotate(${finalAngle}rad)`;
    });

    const onEnd = (ev) => {
      if (ev && ev.propertyName && ev.propertyName !== "transform") return;
      el.removeEventListener("transitionend", onEnd);
      angleRef.current = finalAngle;
      animatingRef.current = false;
      if (typeof onSpinEnd === "function") onSpinEnd();
    };
    el.addEventListener("transitionend", onEnd);

    // Fallback in case transitionend never fires (some mobile browsers
    // drop the event under memory pressure).
    const fallback = setTimeout(onEnd, duration + 200);
    return () => {
      clearTimeout(fallback);
      el && el.removeEventListener("transitionend", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinningTo]);

  // Geometry of a single slice as an SVG path — a pie wedge from the
  // wheel center out to `r`, spanning `arc` radians starting at `start`.
  // Because our SVG viewBox is centered on (0, 0), no translation math
  // is needed anywhere else in this file.
  const R = 200; // wheel radius (viewBox units)
  const RIM = 224;

  const wedgePath = (i) => {
    const start = i * arc - Math.PI / 2 - arc / 2 + Math.PI / 2;
    // We build the wedge in "wheel-local" coordinates so the whole SVG
    // group can be rotated as one. Wedge i starts at angle i*arc and
    // spans `arc` radians measured clockwise from 3 o'clock.
    const a0 = i * arc;
    const a1 = a0 + arc;
    const x0 = Math.cos(a0) * R;
    const y0 = Math.sin(a0) * R;
    const x1 = Math.cos(a1) * R;
    const y1 = Math.sin(a1) * R;
    const largeArc = arc > Math.PI ? 1 : 0;
    // eslint-disable-next-line no-unused-vars
    void start;
    return `M 0 0 L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
  };

  // Position labels on each slice — placed along the slice center-line
  // at ~62% of the radius, rotated so text reads outward from the hub.
  const labelPos = (i) => {
    const a = i * arc + arc / 2;
    const rr = R * 0.62;
    return {
      x: Math.cos(a) * rr,
      y: Math.sin(a) * rr,
      // SVG angles are degrees; we want text to read horizontally at 3
      // o'clock and rotate with the slice, staying upright on the left half.
      rot: (a * 180) / Math.PI,
    };
  };

  /**
   * Compute display metadata for a slice label so it always fits inside
   * its wedge, regardless of how many slices there are or how long the
   * label is. Combines three defenses:
   *
   *   1. Length-tiered base font size (short prizes celebratory-big,
   *      long prizes step down) — mirrors PrizeRevealModal's approach.
   *   2. Slice-count scaling — more slices = narrower wedges = smaller.
   *   3. Two-line wrapping at nearest space to the middle when the
   *      single-line width would exceed the radial budget. Both lines
   *      are then verified against the chord width (tangential space).
   *   4. Final fallback: monotonic font-size downscaling, then
   *      ellipsis truncation, so text can NEVER visually overflow.
   *
   * Notes on geometry (viewBox units, wheel radius R=200, hub r=52):
   *   • Text baseline runs radially outward along the slice center-line,
   *     so its "width" is bounded by the RADIAL budget (hub → inner rim).
   *   • Multiple lines stack perpendicular to the text baseline (tangential),
   *     bounded by the wedge CHORD at the label radius.
   */
  const HUB_R = 52;
  const SAFE_INNER_MARGIN = 22;      // keep text off decorative rim/dividers
  const LABEL_RR = R * 0.62;         // radial position of label center

  const computeLabelMeta = (rawLabel) => {
    const label = String(rawLabel || "").trim();
    if (!label) return { lines: [""], fontSize: 14 };

    // -- Tier 1: base size by character length ------------------------
    const len = label.length;
    let baseSize;
    if (len <= 8) baseSize = 19;
    else if (len <= 14) baseSize = 16;
    else if (len <= 20) baseSize = 14;
    else baseSize = 12;

    // -- Tier 2: slice-count scaling ---------------------------------
    // Narrower wedges need smaller type so the tangential stack of two
    // lines still fits inside the chord.
    const sliceScale =
      n <= 4 ? 1.15
      : n <= 6 ? 1.06
      : n <= 8 ? 1.0
      : n <= 10 ? 0.86
      : n <= 12 ? 0.76
      : 0.68;
    let fontSize = Math.round(baseSize * sliceScale);
    fontSize = Math.max(9, Math.min(22, fontSize));

    // -- Budgets -----------------------------------------------------
    // Radial budget: from hub outer edge to inner rim, minus safety.
    const radialBudget = R - HUB_R - SAFE_INNER_MARGIN;
    // Chord budget at label radius, 80% for safety around dividers.
    const chordBudget = 2 * LABEL_RR * Math.sin(arc / 2) * 0.80;
    // Empirical average character width for Inter 700 weight in SVG units.
    const charW = (size) => size * 0.56;
    const measure = (str, size) => str.length * charW(size);

    // -- Case A: fits on one line ------------------------------------
    if (measure(label, fontSize) <= radialBudget) {
      return { lines: [label], fontSize };
    }

    // -- Case B: try 2-line wrap at nearest space to midpoint --------
    const spaces = [];
    for (let i = 0; i < label.length; i += 1) {
      if (label[i] === " ") spaces.push(i);
    }
    if (spaces.length > 0) {
      const mid = label.length / 2;
      let bestIdx = spaces[0];
      let bestScore = Math.abs(bestIdx - mid);
      for (const s of spaces) {
        const score = Math.abs(s - mid);
        if (score < bestScore) { bestScore = score; bestIdx = s; }
      }
      const line1 = label.slice(0, bestIdx).trim();
      const line2 = label.slice(bestIdx + 1).trim();

      // Ensure BOTH lines fit radially AND the 2-line stack fits
      // inside the chord. Downscale font monotonically until they do.
      const fits = (size) => {
        const maxLine = Math.max(line1.length, line2.length);
        return maxLine * charW(size) <= radialBudget
          && (2 * size * 1.05) <= chordBudget;
      };
      while (fontSize > 9 && !fits(fontSize)) fontSize -= 1;
      if (fits(fontSize)) {
        return { lines: [line1, line2], fontSize };
      }
      // If even at minSize the 2-line stack overflows the chord (very
      // narrow wedges), collapse back to a single line with truncation.
    }

    // -- Case C: single word / no fitting split — downscale then truncate.
    while (fontSize > 9 && measure(label, fontSize) > radialBudget) fontSize -= 1;
    if (measure(label, fontSize) <= radialBudget) {
      return { lines: [label], fontSize };
    }
    const maxChars = Math.max(3, Math.floor(radialBudget / charW(fontSize)) - 1);
    return { lines: [label.slice(0, maxChars).trimEnd() + "…"], fontSize };
  };

  // Small light "bulbs" studded around the outer rim.
  const bulbs = useMemo(() => {
    const count = 24;
    const out = [];
    for (let i = 0; i < count; i += 1) {
      const a = (i / count) * Math.PI * 2;
      out.push({
        cx: Math.cos(a) * RIM,
        cy: Math.sin(a) * RIM,
        // Every other bulb is dimmer for a subtle chase pattern.
        dim: i % 2 === 1,
      });
    }
    return out;
  }, []);

  return (
    <div
      className="relative mx-auto"
      style={{ width: "min(92vw, 440px)", aspectRatio: "1 / 1" }}
      data-testid={`${testId}-container`}
    >
      {/* Soft radial glow behind the wheel — extends the "spotlight"
          feeling required by the design brief. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(245,168,0,0.28), rgba(245,168,0,0) 70%)",
          filter: "blur(6px)",
        }}
      />

      <svg
        viewBox="-260 -260 520 520"
        className="w-full h-full drop-shadow-[0_30px_50px_rgba(0,0,0,0.45)]"
        role="img"
        aria-label={`Spin & Win wheel with ${n} rewards`}
        data-testid={testId}
      >
        <defs>
          <radialGradient id="rw-hub" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="55%" stopColor={CITYDOOH_GOLD} />
            <stop offset="100%" stopColor={CITYDOOH_GOLD_DEEP} />
          </radialGradient>
          <radialGradient id="rw-rim" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor={CITYDOOH_GOLD} />
            <stop offset="100%" stopColor={CITYDOOH_GOLD_DEEP} />
          </radialGradient>
          <filter id="rw-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.6" />
          </filter>
        </defs>

        {/* Outer gold rim with soft bulbs studded around it. */}
        <circle
          cx="0"
          cy="0"
          r={RIM + 8}
          fill={CITYDOOH_INK}
          stroke={CITYDOOH_GOLD}
          strokeWidth="2"
        />
        <circle cx="0" cy="0" r={RIM - 4} fill="url(#rw-rim)" />
        <circle
          cx="0"
          cy="0"
          r={RIM - 14}
          fill={CITYDOOH_INK}
        />
        {bulbs.map((b, i) => (
          <g key={i}>
            <circle
              cx={b.cx}
              cy={b.cy}
              r="6"
              fill="#fff8cc"
              opacity={b.dim ? 0.45 : 0.9}
              filter="url(#rw-glow)"
            />
            <circle
              cx={b.cx}
              cy={b.cy}
              r="3.2"
              fill="#fffdf0"
              opacity={b.dim ? 0.55 : 1}
            />
          </g>
        ))}

        {/* SPINNING GROUP — everything inside rotates as one. */}
        <g
          ref={wheelRef}
          style={{
            transformOrigin: "0 0",
            willChange: "transform",
          }}
        >
          {slices.map((s, i) => {
            const pos = labelPos(i);
            const fill = palette[i];
            // Use the SafeColors-derived on-slice text color when we
            // have one; otherwise pick black-or-white from luminance.
            const isLight = fill === CITYDOOH_GOLD || fill === "#ffffff" || fill === "#FFFFFF";
            const textFill = onSliceText || (isLight ? CITYDOOH_INK : "#ffffff");
            const label = String(s.label || s.gift_name || "").trim();
            const meta = computeLabelMeta(label);
            const { lines, fontSize } = meta;
            const lineH = fontSize * 1.05;
            // Vertical offset so both lines are visually centered around
            // the label point (single line uses y=0 via dominantBaseline).
            const startY = lines.length > 1 ? -((lines.length - 1) * lineH) / 2 : 0;
            const flipped = pos.rot > 90 && pos.rot < 270;
            return (
              <g key={i}>
                <path
                  d={wedgePath(i)}
                  fill={fill}
                  stroke="rgba(0,0,0,0.35)"
                  strokeWidth="1.2"
                />
                <g transform={`translate(${pos.x} ${pos.y}) rotate(${pos.rot})`}>
                  {/* Flip text upright on the left half of the wheel. */}
                  <text
                    x="0"
                    y={startY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontFamily='"Inter", "Figtree", system-ui, sans-serif'
                    fontWeight="700"
                    fontSize={fontSize}
                    fill={textFill}
                    transform={flipped ? "rotate(180)" : ""}
                    style={{ letterSpacing: "0.02em" }}
                  >
                    {lines.map((line, li) => (
                      <tspan key={li} x="0" dy={li === 0 ? 0 : lineH}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Divider lines from center out — subtle depth. */}
          {Array.from({ length: n }).map((_, i) => {
            const a = i * arc;
            return (
              <line
                key={`div-${i}`}
                x1="0"
                y1="0"
                x2={(Math.cos(a) * R).toFixed(2)}
                y2={(Math.sin(a) * R).toFixed(2)}
                stroke="rgba(0,0,0,0.28)"
                strokeWidth="1.2"
              />
            );
          })}
        </g>

        {/* Center hub — DOES NOT rotate. Sits above the wheel group so
            the vendor logo stays upright regardless of spin position. */}
        <g>
          <circle
            cx="0"
            cy="0"
            r="52"
            fill="rgba(0,0,0,0.35)"
            filter="url(#rw-glow)"
          />
          <circle
            cx="0"
            cy="0"
            r="46"
            fill="url(#rw-hub)"
            stroke={CITYDOOH_INK}
            strokeWidth="2"
          />
          {logoUrl && logoOk ? (
            <>
              {/* Clip logo to the inner hub circle. */}
              <defs>
                <clipPath id="rw-hub-clip">
                  <circle cx="0" cy="0" r="38" />
                </clipPath>
              </defs>
              <image
                href={assetUrl(logoUrl)}
                x="-38"
                y="-38"
                width="76"
                height="76"
                preserveAspectRatio="xMidYMid slice"
                clipPath="url(#rw-hub-clip)"
                onError={() => setLogoOk(false)}
              />
            </>
          ) : (
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="middle"
              fontFamily='"Anton", "Impact", sans-serif'
              fontSize="26"
              fill={CITYDOOH_INK}
              letterSpacing="1.2"
            >
              SPIN
            </text>
          )}
        </g>
      </svg>

      {/* Fixed 12-o'clock pointer — outside the rotating group so it stays
          put during spins. Drop it slightly OVER the rim so the tip visually
          overlaps the wedge boundary. */}
      <div
        data-testid="wheel-pointer"
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{ top: "-2%" }}
      >
        <svg width="52" height="66" viewBox="0 0 52 66" aria-hidden="true">
          <defs>
            <linearGradient id="ptrGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff2b8" />
              <stop offset="45%" stopColor={CITYDOOH_GOLD} />
              <stop offset="100%" stopColor={CITYDOOH_GOLD_DEEP} />
            </linearGradient>
          </defs>
          <path
            d="M26 64 L4 22 Q4 4 26 4 Q48 4 48 22 Z"
            fill="url(#ptrGrad)"
            stroke={CITYDOOH_INK}
            strokeWidth="2"
          />
          {/* Inner highlight — makes the pointer look chunky/metallic. */}
          <path
            d="M26 56 L12 24 Q12 12 26 12 Q40 12 40 24 Z"
            fill="rgba(0,0,0,0.12)"
          />
          <circle cx="26" cy="20" r="4" fill="#fff8cc" />
        </svg>
      </div>
    </div>
  );
};

export default RewardWheel;
